import React, { useState, useCallback, useRef, useEffect } from 'react';

interface SelectionPoint {
  day: number;
  block: number;
}

interface ScheduleGridProps<T> {
  daysOfWeek: string[];
  totalBlocks: number;
  blockAssignments: T[][];
  onBlockChange: (day: number, block: number, isAssigned: boolean) => void;
  blockRender: (props: {
    day: number;
    block: number;
    isSelected: boolean;
    isDragging: boolean;
    assignment: T;
  }) => React.ReactNode;
  selectionEnabled: boolean;
}

export function ScheduleGrid<T>({
  daysOfWeek,
  totalBlocks,
  blockAssignments,
  onBlockChange,
  blockRender,
  selectionEnabled,
}: ScheduleGridProps<T>) {
  const [isSelecting, setIsSelecting] = useState(false);
  const [dragStartValue, setDragStartValue] = useState<boolean | null>(null);
  const [selectionStart, setSelectionStart] = useState<SelectionPoint | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<SelectionPoint | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const getBlockFromCoordinates = (x: number, y: number): SelectionPoint | null => {
    if (!gridRef.current) return null;

    const gridRect = gridRef.current.getBoundingClientRect();
    // Adjust y coordinate to account for the time labels row
    const timeLabelRowHeight = 20; // Height of the time labels row
    const adjustedY = y - gridRect.top - timeLabelRowHeight - 4; // 4px for margin-bottom
    
    if (adjustedY < 0) return null; // Click was in the time labels row

    const dayHeight = (gridRect.height - timeLabelRowHeight - 4) / daysOfWeek.length;
    const dayIndex = Math.floor(adjustedY / dayHeight);

    if (dayIndex < 0 || dayIndex >= daysOfWeek.length) return null;

    // Get the day element (add 1 to skip the time labels row)
    const dayElement = gridRef.current.children[dayIndex + 1] as HTMLDivElement;
    if (!dayElement) return null;

    const dayRect = dayElement.children[1].getBoundingClientRect();
    const relativeX = x - dayRect.left;
    const blockIndex = Math.floor(relativeX / (dayRect.width / totalBlocks));

    if (blockIndex < 0 || blockIndex >= totalBlocks) return null;

    return { day: dayIndex, block: blockIndex };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!selectionEnabled) return;
    e.preventDefault();
    const startPoint = getBlockFromCoordinates(e.clientX, e.clientY);
    if (startPoint) {
      setIsSelecting(true);
      setSelectionStart(startPoint);
      setSelectionEnd(startPoint);
      setDragStartValue(!!blockAssignments[startPoint.day][startPoint.block]);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelecting || !selectionEnabled) return;
    e.preventDefault();
    const currentPoint = getBlockFromCoordinates(e.clientX, e.clientY);
    if (currentPoint) {
      setSelectionEnd(currentPoint);
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelecting || !selectionEnabled) return;
    e.preventDefault();

    if (selectionStart && selectionEnd) {
      const startDay = Math.min(selectionStart.day, selectionEnd.day);
      const endDay = Math.max(selectionStart.day, selectionEnd.day);
      const startBlock = Math.min(selectionStart.block, selectionEnd.block);
      const endBlock = Math.max(selectionStart.block, selectionEnd.block);

      const newValue = !dragStartValue;
      for (let day = startDay; day <= endDay; day++) {
        for (let block = startBlock; block <= endBlock; block++) {
          onBlockChange(day, block, newValue);
        }
      }
    }

    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    setDragStartValue(null);
  };

  const isBlockSelected = useCallback(
    (day: number, block: number): boolean => {
      if (!isSelecting || !selectionStart || !selectionEnd) return false;
      const startDay = Math.min(selectionStart.day, selectionEnd.day);
      const endDay = Math.max(selectionStart.day, selectionEnd.day);
      const startBlock = Math.min(selectionStart.block, selectionEnd.block);
      const endBlock = Math.max(selectionStart.block, selectionEnd.block);
      return day >= startDay && day <= endDay && block >= startBlock && block <= endBlock;
    },
    [isSelecting, selectionStart, selectionEnd]
  );

  return (
    <div
      ref={gridRef}
      className="w-full"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Time labels row */}
      <div className="flex w-full items-center gap-4 mb-1">
        <div className="w-24 shrink-0"></div>
        <div className="flex-1 relative" style={{ height: '20px' }}>
          {Array.from({ length: 25 }).map((_, i) => (
            <div 
              key={i} 
              className="absolute text-xs text-gray-400"
              style={{
                left: `${(i * 100) / 24}%`,
                transform: 'translateX(-50%)',
                width: '24px',
                textAlign: 'center'
              }}
            >
              {i < 24 ? i : ''}
            </div>
          ))}
        </div>
      </div>

      {/* Grid rows */}
      {daysOfWeek.map((dayName, dayIndex) => (
        <div key={dayName} className="flex w-full items-center gap-4">
          <div className="w-24 shrink-0 text-gray-400">{dayName}</div>
          <div 
            className="grid h-8 flex-1" 
            style={{
              gridTemplateColumns: `repeat(${totalBlocks}, 1fr)`,
              marginLeft: '-1px',
              width: 'calc(100% + 1px)'
            }}
          >
            {Array.from({ length: totalBlocks }).map((_, blockIndex) => (
              <div
                key={blockIndex}
                className="h-full border-b border-r border-t border-gray-700"
              >
                {blockRender({
                  day: dayIndex,
                  block: blockIndex,
                  isSelected: isBlockSelected(dayIndex, blockIndex),
                  isDragging: isSelecting,
                  assignment: blockAssignments[dayIndex]?.[blockIndex],
                })}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
